import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import api from '../api/api';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import StatCard from '../components/dashboard/StatCard';
import QuickActions from '../components/dashboard/QuickActions';
import RecentActivity from '../components/dashboard/RecentActivity';
import { Button, Icon } from '../components/ui';
import OnboardingTooltip from '../components/onboarding/OnboardingTooltip';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend, Filler);

export default function ProfessionalDashboard() {
  const { isAdmin, isCollaborator } = useAuth();
  const { activeWorkspace, loading: workspaceLoading } = useWorkspace();
  const navigate = useNavigate();

  const [filterPeriod, setFilterPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/dashboard/stats?period=${filterPeriod}`);
      setData(res.data?.data || res.data);
    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
      setError('Impossible de charger le tableau de bord.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (workspaceLoading) return;
    loadDashboardData();
  }, [filterPeriod, workspaceLoading]);

  const handleRefresh = () => { setRefreshing(true); loadDashboardData(); };
  const formatCurrency = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'TND', minimumFractionDigits: 3 }).format(val);

  const handleQuickAction = (actionId) => {
    const routes = {
      settings: '/settings', users: '/users', backup: '/admin/backup',
      add_client: '/clients', add_invoice: '/invoices',
    };
    if (routes[actionId]) navigate(routes[actionId]);
  };

  // Chart datasets mémorisés
  const charts = useMemo(() => {
    if (!data) return null;
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return {
      revenue: {
        labels: months,
        datasets: [{
          label: `Revenus ${new Date().getFullYear()} (DT)`, data: data.revenueChart?.map(d => d.revenue) || months.map(() => 0),
          backgroundColor: 'rgba(59, 130, 246, 0.1)', borderColor: '#3B82F6', borderWidth: 2, tension: 0.4, fill: true,
        }]
      },
      invoices: {
        labels: ['Payées', 'En attente', 'En retard', 'Brouillon'],
        datasets: [{
          data: [data.invoiceStatus?.paid || 0, data.invoiceStatus?.sent || 0, data.invoiceStatus?.overdue || 0, data.invoiceStatus?.draft || 0],
          backgroundColor: ['#10B981', '#3B82F6', '#EF4444', '#9CA3AF'], borderWidth: 0,
        }]
      },
      performance: {
        labels: months,
        datasets: [
          { label: 'Facturé', data: data.performanceChart?.factures?.map(d => d.value) || months.map(() => 0), borderColor: '#3B82F6', tension: 0.4 },
          { label: 'Encaissé', data: data.performanceChart?.encaissements?.map(d => d.value) || months.map(() => 0), borderColor: '#10B981', tension: 0.4 },
        ]
      },
    };
  }, [data]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Icon name="IconLoader" size={32} className="animate-spin text-blue-600" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-gray-50 text-red-600">{error}</div>;
  if (!data) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <div className="bg-white shadow-sm border-b mb-8">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div data-tour="dashboard">
              <h1 className="text-2xl font-bold text-gray-900">Tableau de Bord</h1>
              <p className="text-sm text-gray-500">{new Date().toLocaleDateString('fr-FR', { dateStyle: 'full' })}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[{ id: 'day', label: "Aujourd'hui" }, { id: 'week', label: 'Semaine' }, { id: 'month', label: 'Mois' }, { id: 'year', label: 'Année' }]
                  .map((period) => (
                  <button key={period.id} onClick={() => setFilterPeriod(period.id)}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${filterPeriod === period.id ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}>
                    {period.label}
                  </button>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={handleRefresh}>
                <Icon name="IconSync" size={16} className={refreshing ? 'animate-spin' : ''} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 space-y-8">
        {/* KPI CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <OnboardingTooltip text="Revenu total toutes factures confondues (hors brouillons)" position="bottom">
            <StatCard title="Chiffre d'affaires" value={formatCurrency(data.totalRevenue)} iconName="IconChartLine" color="blue" onClick={() => navigate('/invoices')} />
          </OnboardingTooltip>
          <OnboardingTooltip text="Nombre de clients avec un statut actif" position="bottom">
            <StatCard title="Clients actifs" value={data.activeClients} iconName="IconUsers" color="green" onClick={() => navigate('/clients')} />
          </OnboardingTooltip>
          <OnboardingTooltip text="Montant total des factures envoyées non encore réglées" position="bottom">
            <StatCard title="Solde à recouvrer" value={formatCurrency(data.totalBalance)} iconName="IconWallet" color="purple" onClick={() => navigate('/reports/balance')} />
          </OnboardingTooltip>
          <OnboardingTooltip text="Factures dont la date d'échéance est dépassée" position="bottom">
            <StatCard title="Factures en retard" value={data.overdueInvoices} iconName="IconAlertTriangle" color="red" onClick={() => navigate('/invoices?status=overdue')} />
          </OnboardingTooltip>
        </div>

        {/* GRAPHIQUES */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border h-80">
            <h3 className="font-semibold mb-4 text-gray-800">Évolution des revenus (Année)</h3>
            {charts?.revenue && <Line data={charts.revenue} options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }} />}
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm border h-80">
            <h3 className="font-semibold mb-4 text-gray-800">Performance (Facturé vs Encaissé)</h3>
            {charts?.performance && <Line data={charts.performance} options={{ maintainAspectRatio: false }} />}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border h-80">
            <h3 className="font-semibold mb-4 text-gray-800">État des Factures</h3>
            {charts?.invoices && <Doughnut data={charts.invoices} options={{ maintainAspectRatio: false, cutout: '70%' }} />}
          </div>
          <div className="lg:col-span-2">
            <RecentActivity activities={data.recentActivities || []} limit={5} showViewAll />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border">
            <h3 className="font-semibold mb-4 text-gray-800">Top Clients (CA Validé)</h3>
            <div className="space-y-3">
              {(data.topClients || []).map(c => (
                <div key={c._id} onClick={() => navigate(`/clients/${c._id}`)}
                  className="flex justify-between items-center cursor-pointer hover:bg-gray-50 p-3 rounded-lg border border-gray-100 transition-all">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                      {(c.name || 'C').charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium text-sm text-gray-900">{c.name || 'Client'}</div>
                      <div className="text-xs text-gray-500">{c.invoiceCount} factures</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-sm text-gray-900">{formatCurrency(c.revenue)}</div>
                    {c.soldeActuel > 0 && <div className="text-xs text-red-500 font-medium">Reste: {formatCurrency(c.soldeActuel)}</div>}
                  </div>
                </div>
              ))}
              {(!data.topClients || data.topClients.length === 0) && <p className="text-sm text-gray-400 italic">Aucun client avec Chiffre d'Affaires validé.</p>}
            </div>
          </div>
          <div data-tour="quick-actions">
            <QuickActions variant={isAdmin() ? "admin" : "default"} onActionClick={handleQuickAction} />
          </div>
        </div>
      </div>
    </div>
  );
}
